"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Benefits() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: pillarsRef,
    isVisible: pillarsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const pillars = [{
    title: "Performance-Based Growth",
    body: "We operate on a results-driven model. You only pay for qualified leads and confirmed appointments, ensuring your marketing budget is always tied to tangible business outcomes.",
    icon: <svg viewBox="0 0 48 48" fill="none" className="w-8 h-8 sm:w-9 sm:h-9" aria-hidden="true">
          <path d="M10 32C10 20 18 12 30 12C34 12 38 14 38 14C38 14 38 22 34 28C30 34 22 36 14 34" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 34C18 30 24 24 30 18" stroke="var(--accent)" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10 38C10 36 12 34 14 34" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
  }, {
    title: "Qualified Lead Pipeline",
    body: "Stop wasting time on cold outreach. We provide high-intent, pre-vetted insurance leads ready for your expertise, allowing you to focus entirely on closing deals.",
    icon: <svg viewBox="0 0 48 48" fill="none" className="w-8 h-8 sm:w-9 sm:h-9" aria-hidden="true">
          <path d="M10 28C10 24 12 20 16 20C18 20 20 21 22 22C23 23 24 24 24 24" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M38 28C38 24 36 20 32 20C30 20 28 21 26 22C25 23 24 24 24 24" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 28C16 32 20 36 24 36C28 36 32 32 32 28" stroke="var(--accent)" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M20 26C20 28 22 30 24 30C26 30 28 28 28 26" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
  }, {
    title: "Seamless Scaling",
    body: "Our infrastructure is built to scale with your agency. Whether you need a steady stream of daily appointments or a surge in outreach, we adapt to your growth requirements.",
    icon: <svg viewBox="0 0 48 48" fill="none" className="w-8 h-8 sm:w-9 sm:h-9" aria-hidden="true">
          <path d="M24 38V18" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          <path d="M24 22C20 22 16 20 14 16C18 14 22 16 24 20" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M24 26C28 26 32 24 34 20C30 18 26 20 24 24" stroke="var(--accent)" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M24 30C20 30 17 28 15 25" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M24 18C24 14 26 11 30 10" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
  }];
  return <section data-section-id="16057" id="preclosedsalesbenefits" className="bg-background text-foreground py-24 sm:py-32 md:py-40 px-6 md:px-12 lg:px-16 border-t border-border/40">
      <div className="max-w-[1240px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div ref={headingRef} className="lg:col-span-5 lg:sticky lg:top-28" style={{
          opacity: headingVisible ? 1 : 0,
          transform: headingVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 640ms cubic-bezier(0.22, 1, 0.36, 1), transform 640ms cubic-bezier(0.22, 1, 0.36, 1)"
        }}>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] mb-4 text-primary">
              Our Commitment
            </p>
            <h2 className="font-sans text-foreground text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.12]">
              Growth without the{" "}
              <span className="relative inline-block italic">
                <span className="relative z-10">upfront risk</span>
                <svg viewBox="0 0 220 14" className="absolute left-0 right-0" style={{
                bottom: "-0.15em",
                width: "100%",
                height: "0.4em",
                overflow: "visible"
              }} preserveAspectRatio="none" aria-hidden="true">
                  <path d="M3 8 C 40 3, 80 11, 120 6 S 200 4, 217 9" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" fill="none" style={{
                  strokeDasharray: 260,
                  strokeDashoffset: headingVisible ? 0 : 260,
                  transition: "stroke-dashoffset 700ms ease-out 120ms"
                }} />
                </svg>
              </span>{" "}
            </h2>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground font-normal">
              RMA Solutions LLC empowers insurance agents to scale effortlessly. We handle the heavy lifting of lead generation and appointment setting, so you can focus on what you do best: closing policies and building client relationships.
            </p>
          </div>

          <div ref={pillarsRef} className="lg:col-span-7">
            <ul className="flex flex-col gap-6 sm:gap-8">
              {pillars.map((pillar, i) => <li key={pillar.title} data-index={i} className="group relative bg-card border border-border/80 rounded-xl p-6 sm:p-8 lg:p-9 shadow-sm hover:shadow-md hover:border-accent/40 transition-all duration-300" style={{
              opacity: pillarsVisible ? 1 : 0,
              transform: pillarsVisible ? "translateY(0)" : "translateY(16px)",
              transition: `opacity 640ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 120}ms, transform 640ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 120}ms`
            }}>
                  <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-start">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0 text-primary transition-all duration-300 group-hover:bg-accent/20 group-hover:border-accent/40 group-hover:scale-105">
                      {pillar.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground mb-2.5">
                        {pillar.title}
                      </h3>
                      <p className="text-sm sm:text-base leading-relaxed text-muted-foreground font-normal">
                        {pillar.body}
                      </p>
                    </div>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}